import { Search, X } from 'lucide-react'
import { SegmentedControl } from '@/components/ui/controls'
import { Input } from '@/components/ui/field'
import { DatePicker } from './DatePicker'
import type { MeetingDomain, MeetingFilters } from '@/data/types'
import { PeopleFilter, SpaceFilter } from './filters'

/**
 * Upcoming and Past are two lists, not one timeline with a cursor in the
 * middle. That is what lets each side scroll forever in its own direction, and
 * it is why the old Today button is gone: today is simply the top of whichever
 * tab is open. The picker stays for the rarer jump.
 */
export function MeetingsToolbar({
  domain,
  onDomainChange,
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  jumpDate,
  onJump,
  onClearJump,
}: {
  domain: MeetingDomain
  onDomainChange: (domain: MeetingDomain) => void
  search: string
  onSearchChange: (search: string) => void
  filters: MeetingFilters
  onFiltersChange: (filters: MeetingFilters) => void
  jumpDate: string | null
  onJump: (ymd: string) => void
  onClearJump: () => void
}) {
  const active =
    filters.people.participant.length +
    filters.people.access.length +
    filters.people.shared.length +
    filters.spaces.length

  const toggleSpace = (id: string) =>
    onFiltersChange({
      ...filters,
      spaces: filters.spaces.includes(id)
        ? filters.spaces.filter((item) => item !== id)
        : [...filters.spaces, id],
    })

  return (
    // The search field takes whatever the fixed controls leave, so the row
    // spans the same width as the list beneath it instead of trailing off into
    // empty space on the right.
    <div className="flex flex-wrap items-center gap-2">
      <SegmentedControl
        value={domain}
        onValueChange={onDomainChange}
        options={[
          { value: 'past', label: 'Past' },
          { value: 'upcoming', label: 'Upcoming' },
        ]}
      />

      <div className="relative min-w-48 flex-1">
        <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search meetings and people…"
          aria-label="Search meetings"
          className="h-8 pr-7 pl-8 text-xs"
        />
        {search ? (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            aria-label="Clear search"
            className="absolute top-1/2 right-1.5 grid size-5 -translate-y-1/2 place-items-center rounded-[var(--radius-xs)] text-[var(--color-fg-subtle)] outline-none hover:text-[var(--color-fg)]"
          >
            <X className="size-3" />
          </button>
        ) : null}
      </div>

      <DatePicker domain={domain} value={jumpDate} onSelect={onJump} onClear={onClearJump} />

      <PeopleFilter
        people={filters.people}
        onChange={(people) => onFiltersChange({ ...filters, people })}
      />

      {/* Multi-select, because "the two live deals" is a real question and the
          chat's single-space scope cannot ask it. */}
      <SpaceFilter selected={filters.spaces} onToggle={toggleSpace} />

      {active > 0 ? (
        <button
          type="button"
          onClick={() =>
            onFiltersChange({
              ...filters,
              people: { participant: [], access: [], shared: [] },
              spaces: [],
            })
          }
          className="inline-flex h-8 items-center gap-1 rounded-[var(--radius-sm)] px-1.5 text-2xs text-[var(--color-fg-subtle)] outline-none hover:text-[var(--color-fg)]"
        >
          <X className="size-3" />
          Clear
        </button>
      ) : null}
    </div>
  )
}
